<template>
	<div class="MediaFeed" :class="reverse ? 'MediaFeed--reverse' : ''">
		<div class="SectionHeading" :class="reverse ? 'SectionHeading--reverse' : ''">
			<span class="SectionHeading-heading">
				<h3 class="Heading Heading--h3">{{ title }}</h3>
			</span>
			<span v-if="filter" class="SectionHeading-filter">{{ filter }}</span>
			<span v-if="moreLink && moreLabel" class="SectionHeading-link">
				<a :href="moreLink">{{ moreLabel }}</a>
			</span>
			<span v-if="numPages > 1" class="SectionHeading-link SectionHeading-link--right">
				<ol class="MediaFeed-dots">
					<li v-for="(page, index) in numPages" :key="index">
						<button class="MediaFeed-dot" type="button" :class="{ active: currentPage === index }" @click="goToPage(index)">
							<span class="is-visuallyHidden">{{ page }}</span>
						</button>
					</li>
				</ol>
				<span class="MediaFeed-nav">
					<button type="button" :disabled="currentPage === 0" @click="previous">
						<CloIcon symbol="arrowLeft" />
						<span class="is-visuallyHidden">{{ $t('generic.Previous') }}</span>
					</button>
					<button type="button" :disabled="currentPage == numPages - 1" @click="next">
						<CloIcon symbol="arrowRight" />
						<span class="is-visuallyHidden">{{ $t('generic.Next') }}</span>
					</button>
				</span>
			</span>
		</div>
		<ol
			v-if="mediaList.length"
			class="MediaFeed-slides"
			:style="scrollsnap ? 'scroll-snap-type: x mandatory;' : ''"
			@scroll="handleScroll"
			ref="carousel"
		>
			<li
				v-for="(item, index) in mediaList"
				:key="index"
				class="MediaFeed-slide"
				:style="'flex-basis: ' + slideWidth + 'px'"
				tabindex="-1"
				ref="slides"
			>
				<!-- dataFormat search1.0 -->
				<figure v-if="dataFormat !== 'search2.0'" class="Media Media--flush u-margin-none">
					<a class="Media-content" :href="item.citationUrl" @click.prevent="openLightbox(item.assetId)">
						<img
							v-if="item.mediaType === 'p'"
							:alt="item.title || item.comName"
							:src="mediaDownloadUrl + item.assetId + '/320'"
							:srcset="
								mediaDownloadUrl +
								item.assetId +
								'/160 160w,' +
								mediaDownloadUrl +
								item.assetId +
								'/480 480w,' +
								mediaDownloadUrl +
								item.assetId +
								'/640 640w,' +
								mediaDownloadUrl +
								item.assetId +
								'/900 900w'
							"
							sizes="14rem"
							data-image-loaded
						/>
						<img
							v-if="item.mediaType === 'a'"
							class="MediaFeed-spectrogram"
							:alt="item.title || item.comName"
							:src="mediaDownloadUrl + item.assetId + spectrogramPreviewPath"
							data-image-loaded
						/>
						<img
							v-if="item.mediaType === 'v'"
							:alt="item.title || item.comName"
							:src="mediaDownloadUrl + item.assetId + videoPreviewPath"
							data-image-loaded
						/>
						<div v-if="item.mediaType !== 'p'" class="MediaFeed-playButton" aria-hidden="true">
							<CloIcon symbol="play" />
						</div>
					</a>
					<figcaption class="Media-caption">
						<div class="MediaFeed-speciesLabel">
							<span v-if="item.valid === 'false'" class>
								<CloIcon symbol="warning" size="sm" />
								<span class="is-visuallyHidden">{{ $t('mediaSearch.unconfirmed') }}</span>
							</span>
							<span class="Heading Heading--h5 u-margin-none" :class="reverse ? 'Heading--reverse' : ''">
								<a v-if="item.speciesUrl" :href="item.speciesUrl">
									<span class="Heading-main">{{ item.comName }}</span>
									<span class="Heading-sub Heading-sub--sci Heading-sub--inline">{{ item.sciName }}</span>
								</a>
								<template v-else>
									<span class="Heading-main">{{ item.comName }}</span>
									<span class="Heading-sub Heading-sub--sci Heading-sub--inline">{{ item.sciName }}</span>
								</template>
							</span>
							<span v-if="item.exoticCategory">
								<button type="button" :title="exoticTitle(item.exoticCategory)" v-clo-tooltip @click="openExoticModal">
									<CloIcon size="sm" :symbol="exoticSymbol(item.exoticCategory)" />
								</button>
							</span>
						</div>
						<div class="MediaFeed-user" v-if="item.userDisplayName">&copy; {{ item.userDisplayName }}</div>
						<div class="MediaFeed-date" v-if="item.date">
							<span>{{ item.date }}</span>
						</div>
						<div class="MediaFeed-location" v-if="item.location">
							<span>{{ item.location }}</span>
						</div>
					</figcaption>
				</figure>

				<!-- dataFormat search2.0 -->
				<figure v-else class="Media Media--flush u-margin-none">
					<a class="Media-content" :href="mediaSpecimenUrl + item.assetId" @click.prevent="openLightbox(item.assetId)">
						<img
							v-if="item.mediaType === 'photo'"
							:alt="item.assetTitle"
							:src="mediaDownloadUrl + item.assetId + '/320'"
							:srcset="
								mediaDownloadUrl +
								item.assetId +
								'/160 160w,' +
								mediaDownloadUrl +
								item.assetId +
								'/480 480w,' +
								mediaDownloadUrl +
								item.assetId +
								'/640 640w,' +
								mediaDownloadUrl +
								item.assetId +
								'/900 900w'
							"
							sizes="14rem"
							data-image-loaded
						/>
						<img
							v-if="item.mediaType === 'audio'"
							class="MediaFeed-spectrogram"
							:class="{ pixelated: loadedImages.includes(item.assetId) }"
							:alt="item.assetTitle"
							:src="mediaDownloadUrl + item.assetId + spectrogramPreviewPath"
							data-image-loaded
							@load="loadedImages.push(item.assetId)"
						/>
						<img
							v-if="item.mediaType === 'video'"
							:alt="item.assetTitle"
							:src="mediaDownloadUrl + item.assetId + videoPreviewPath"
							data-image-loaded
						/>
						<img
							v-if="item.mediaType === 'eod'"
							:alt="item.assetTitle"
							:src="mediaDownloadUrl + item.assetId + '/1500'"
							data-image-loaded
						/>
						<div v-if="item.mediaType !== 'photo' && item.mediaType !== 'eod'" class="MediaFeed-playButton" aria-hidden="true">
							<CloIcon symbol="play" />
						</div>
					</a>
					<figcaption class="Media-caption">
						<div class="MediaFeed-speciesLabel">
							<span v-if="!item.valid" class>
								<CloIcon symbol="warning" size="sm" />
								<span class="is-visuallyHidden">{{ $t('mediaSearch.unconfirmed') }}</span>
							</span>
							<span v-if="item.tags && item.tags.includes('person_detected')" class>
								<CloIcon symbol="users" size="sm" />
								<span class="is-visuallyHidden">{{ $t('mediaSearch.notPublic') }}</span>
							</span>
							<span class="Heading Heading--h5 u-margin-none" :class="reverse ? 'Heading--reverse' : ''">
								<template v-if="item.taxonomy.speciesCode">
								<a v-if="item.speciesUrl" :href="item.speciesUrl">
									<span class="Heading-main">{{ item.taxonomy.comName }}</span>
									<span class="Heading-sub Heading-sub--sci Heading-sub--inline">{{ item.taxonomy.sciName }}</span>
								</a>
								<template v-else>
									<span class="Heading-main">{{ item.taxonomy.comName }}</span>
									<span class="Heading-sub Heading-sub--sci Heading-sub--inline">{{ item.taxonomy.sciName }}</span>
								</template>
								</template>
								<template>
									<span class="Heading-main MediaFeed-lineClamp">{{ assetTitle(item, false) }}</span>
								</template>
							</span>
							<span v-if="item.exoticCategory">
								<button type="button" :title="exoticTitle(item.exoticCategory)" v-clo-tooltip @click="openExoticModal">
									<CloIcon size="sm" :symbol="exoticSymbol(item.exoticCategory)" />
								</button>
							</span>
						</div>
						<div class="MediaFeed-user" v-if="item.userDisplayName">&copy; {{ item.userDisplayName }}</div>
						<div class="MediaFeed-date" v-if="item.obsDt">
							<span>{{ item.obsDtDisplay }}</span>
						</div>
						<div class="MediaFeed-location" v-if="item.locationDisplay">
							<span>{{ item.locationDisplay }}</span>
						</div>
					</figcaption>
				</figure>
			</li>
		</ol>
		<div v-else class="MediaFeed-noMedia" :class="reverse ? 'u-colorReverse' : ''">
			<div>
				<!-- @slot for the media feed-->
				<slot />
			</div>
		</div>
		<CloLightbox
			reverse
			:list="list"
			:source="source"
			:dataFormat="dataFormat"
			:mediaDownloadUrl="mediaDownloadUrl"
			:mediaSpecimenUrl="mediaSpecimenUrl"
			:ebirdWebUrl="ebirdWebUrl"
			:ratingEnabled="ratingEnabled"
			:getRatingsUrl="getRatingsUrl"
			:saveRatingUrl="saveRatingUrl"
			:ratingCsrf="ratingCsrf"
			v-if="lightboxOpen"
			v-on:close="closeLightbox"
			:goTo="goToAsset"
			:mediaVersion="mediaVersion"
		/>
	</div>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloSectionHeading from '@/layouts/CloSectionHeading/CloSectionHeading.vue';
import CloMedia from '@/media/CloMedia/CloMedia.vue';
import CloLightbox from '@/media/CloLightbox/CloLightbox.vue';

export default {
	components: {
		CloIcon,
		CloHeading,
		CloSectionHeading,
		CloMedia,
		CloLightbox,
	},
	data() {
		return {
			scrollTimer: false,
			debounceTime: 100,
			scrollsnap: true,
			slidesPerPage: 2,
			slideWidth: 160,
			numPages: 1,
			currentPage: 0,
			lightboxOpen: false,
			goToAsset: null,
			loadedImages: [],
		};
	},
	props: {
		reverse: {
			type: Boolean,
			default: false,
		},
		source: {
			type: String,
			default: 'search',
		},
		/**
		 * The expected data format of each asset in list
		 * This corresponds to Macaulay Library APIs
		 * search = search 1.0
		 * search2.0 = search 2.0
		 * @values search search2.0
		 */
		dataFormat: {
			type: String,
			default: 'search',
		},
		list: {
			type: Array,
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		filter: {
			type: String,
			default: null,
		},
		moreLink: {
			type: String,
			default: null,
		},
		moreLabel: {
			type: String,
			default: null,
		},
		mediaDownloadUrl: {
			default: 'https://cdn.download.ams.birds.cornell.edu/api/v1/asset/',
			type: String,
		},
		mediaSpecimenUrl: {
			default: 'https://macaulaylibrary.org/asset/',
			type: String,
		},
		ebirdWebUrl: {
			default: 'https://ebird.org',
			type: String,
		},
		ratingEnabled: {
			type: Boolean,
			default: false,
		},
		getRatingsUrl: {
			type: String,
			default: '/rating-api?assetIds=',
		},
		saveRatingUrl: {
			type: String,
			default: '/rating-api',
		},
		ratingApiHeader: {
			type: String,
			default: null,
		},
		ratingCsrf: {
			type: String,
			default: null,
		},
		/**
		 * The number of assets to display
		 * while keeping full list accessible in lightbox
		 */
		limit: {
			type: Number,
			default: 20,
		},
		mediaVersion: {
			type: String,
			default: '1.0',
		},
	},
	computed: {	
		spectrogramPreviewPath() {
			if (this.mediaVersion === '2.0') {
				return '/default/preview';
			}
			return '/poster';
		},
		videoPreviewPath() {
			if (this.mediaVersion === '2.0') {
				return '/640';
			}
			return '/thumb';
		},
		direction() {
			if (document.querySelector('html').getAttribute('dir') === 'rtl') {
				return 'rtl';
			} else {
				return 'ltr';
			}
		},
		classes() {
			const classes = [];
			const prefix = 'MediaFeed--';
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			return classes.join(' ');
		},
		mediaList() {
			const newList = [];
			if (this.dataFormat === 'search2.0') {
				return this.list.map((item) => {
					// set eBird species links
					const isBird =
						item.taxonomy.speciesCode &&
						item.taxonomy.speciesCode.indexOf('t-') !== 0 &&
						item.taxonomy.speciesCode.indexOf('f-') !== 0;
					const speciesOrSubspecies = ['species', 'issf', 'form'].includes(item.taxonomy.category);
					item.speciesUrl = isBird && speciesOrSubspecies ? this.ebirdWebUrl + '/species/' + item.taxonomy.reportAs : null;
					// set eBird profile links
					if (item.userHasProfile) {
						const userId = item.userId.replace('USER', '');
						item.userProfileUrl = this.ebirdWebUrl + '/profile/' + btoa(userId);
					}
					// set location string
					item.locationDisplay = [
						item.location.name,
						item.location.locality,
						item.location.subnational2Name,
						item.location.subnational1Name,
						item.location.countryName,
					]
						.filter((loc) => {
							return loc !== null;
						})
						.join(', ');
					item.assetTitle = this.assetTitle(item);
					return item;
				}).filter((item, index) => {
					return !this.limit || index < this.limit;
				});
			}

			// this converts search1.0 and gallery3.0 data formats
			// support for these will be eventually deprecated
			this.list.forEach((item) => {
				if (this.limit && newList.length >= this.limit) {
					return;
				}
				const newItem = {};
				if (this.source === 'search') {
					newItem.assetId = item.assetId;
					newItem.source = item.source;
					newItem.valid = item.valid;
					newItem.comName = item.commonName;
					newItem.sciName = item.sciName;
					newItem.userDisplayName = item.userDisplayName;
					newItem.userProfileUrl = item.userProfileUrl;
					newItem.date = item.obsDttm;
					newItem.location = item.location;
					newItem.mediaType = item.mediaType.slice(0, 1).toLowerCase();
					newItem.speciesUrl = item.ebirdSpeciesUrl;
					newItem.citationName = 'ML' + item.assetId;
					newItem.citationUrl = item.specimenUrl;
					newItem.checklistUrl = item.eBirdChecklistUrl;
					newItem.checklistId = item.eBirdChecklistId;
					newItem.age = item.age;
					newItem.sex = item.sex;
					newItem.exoticCategory = item.exoticCategory;
					if (item.rating) {
						newItem.rating = Math.round(parseInt(item.rating));
					}
					if (item.ratingCount) {
						newItem.ratingCount = Math.round(parseInt(item.ratingCount));
					}
					newItem.details = item.comments;
					if (newItem.details && newItem.details.length > 80) {
						newItem.details = newItem.details.slice(0, 80) + '...';
					}
					newItem.tags = {};
					if (item.subjectData[0].subjectTags.sounds) {
						newItem.tags.sounds = item.subjectData[0].subjectTags.sounds
							.map((x) => {
								return x.localizedString;
							})
							.join('; ');
					}
					if (item.subjectData[0].subjectTags.behaviors) {
						newItem.tags.behaviors = item.subjectData[0].subjectTags.behaviors
							.map((x) => {
								return x.localizedString;
							})
							.join('; ');
					}
					if (item.subjectData[0].subjectTags.breeding) {
						newItem.tags.breeding = item.subjectData[0].subjectTags.breeding
							.map((x) => {
								return x.localizedString;
							})
							.join('; ');
					}
					if (item.subjectData[0].subjectTags.tags_subject) {
						newItem.tags.tags = item.subjectData[0].subjectTags.tags_subject
							.map((x) => {
								return x.localizedString;
							})
							.join('; ');
					}
					if (item.source === 'ml') {
						if (item.subjectData[0].ageSexCounts) {
							newItem.ageSexCounts = item.subjectData[0].ageSexCounts
								.map((x) => {
									return x.localizedString;
								})
								.join('; ');
						}
					}
				}
				if (this.source === 'gallery') {
					newItem.title = item.title;
					newItem.caption = item.caption;
					newItem.comName = item.asset.comName;
					newItem.sciName = item.asset.sciName;
					newItem.assetId = item.asset.assetId.toString();
					newItem.source = item.source;
					newItem.date = item.asset.obsDt;
					newItem.isMap = item.asset.isMap;
					if (item.asset.mediaContentType.indexOf('audio') !== -1) {
						newItem.mediaType = 'a';
					} else if (item.asset.mediaContentType.indexOf('video') !== -1) {
						newItem.mediaType = 'v';
					} else {
						newItem.mediaType = 'p';
					}
					const locArray = [];
					if (item.asset.hasOwnProperty('stateName') && item.asset.stateName) {
						locArray.push(item.asset.stateName);
					}
					if (item.asset.hasOwnProperty('countryName') && item.asset.countryName) {
						locArray.push(item.asset.countryName);
					}
					newItem.location = locArray.join(', ');
					if (item.asset.hasOwnProperty('ebirdChecklistId')) {
						newItem.checklistId = item.asset.ebirdChecklistId;
						newItem.checklistUrl = this.ebirdWebUrl + '/checklist/' + item.checklistId;
					}
					newItem.restricted = item.asset.restricted;
					newItem.credit = item.asset.credit;
					if (item.asset.citationUrl) {
						newItem.citationurl = item.asset.citationUrl;
						newItem.userDisplayName = item.asset.creatorDisplayName;
					} else {
						newItem.citationurl = false;
						newItem.userDisplayName = item.asset.userDisplayName;
					}
				}
				if (this.source === 'gallery3.0') {
					newItem.title = item.title;
					newItem.caption = item.caption;
					newItem.comName = item.asset.comName;
					newItem.sciName = item.asset.sciName;
					newItem.assetId = item.asset.assetId.toString();
					newItem.source = item.source;
					newItem.date = item.asset.obsDtDisplay;
					newItem.isMap = item.asset.isMap;
					newItem.mediaType = item.asset.assetFormatCode.slice(0, 1).toLowerCase();
					const locArray = [];
					if (item.asset.hasOwnProperty('locName') && item.asset.locName) {
						locArray.push(item.asset.locName);
					}
					if (item.asset.hasOwnProperty('subnational2Name') && item.asset.subnational2Name) {
						locArray.push(item.asset.subnational2Name);
					}
					if (item.asset.hasOwnProperty('subnational1Name') && item.asset.subnational1Name) {
						locArray.push(item.asset.subnational1Name);
					}
					if (item.asset.hasOwnProperty('countryName') && item.asset.countryName) {
						locArray.push(item.asset.countryName);
					}
					newItem.location = locArray.join(', ');
					if (item.asset.hasOwnProperty('ebirdChecklistId')) {
						newItem.checklistId = item.asset.ebirdChecklistId;
						newItem.checklistUrl = this.ebirdWebUrl + '/checklist/' + item.checklistId;
					}
					if (item.asset.hasOwnProperty('subId')) {
						newItem.checklistId = item.asset.subId;
						newItem.checklistUrl = this.ebirdWebUrl + '/checklist/' + item.checklistId;
					}
					newItem.restricted = item.asset.restricted;
					newItem.credit = item.asset.credit;
					newItem.speciesUrl = item.ebirdSpeciesUrl;
					if (item.asset.citationUrl) {
						newItem.citationUrl = item.asset.citationUrl;
						newItem.citationName = item.asset.citationName;
					} else if (item.asset.parentAssetId && !item.asset.parentAssetRestricted) {
						newItem.citationUrl = this.mediaSpecimenUrl + item.asset.parentAssetId;
						newItem.citationName = 'ML' + item.asset.parentAssetId;
					}
					newItem.userDisplayName = item.asset.userDisplayName;
				}
				newList.push(newItem);
			});
			return newList;
		},
	},
	mounted() {
		this.setConfiguration();
		this.handleResize();
	},
	methods: {
		setConfiguration() {
			this.slidesPerPage = Math.ceil(this.$el.clientWidth / 240);
			this.slideWidth = this.$el.clientWidth / this.slidesPerPage - 16;
			this.numPages = Math.ceil(this.mediaList.length / this.slidesPerPage);
		},
		previous() {
			this.currentPage--;
			this.animateScrollTo(-1);
		},
		next() {
			this.currentPage++;
			this.animateScrollTo(1);
		},
		goToPage(index) {
			let direction = 1;
			if (this.currentPage > index) {
				direction = -1;
			}
			this.currentPage = index;
			this.animateScrollTo(direction);
		},
		animateScrollTo(direction) {
			this.scrollsnap = false;
			const carousel = this.$refs.carousel;
			const slideWidth = carousel.clientWidth;
			const totalWidth = slideWidth * this.mediaList.length;
			const dir = this.direction === 'rtl' ? -1 : 1;
			const currentPosition = slideWidth * (this.currentPage - direction) * dir;
			const targetPosition = currentPosition + direction * slideWidth * dir;
			const time = 0.5;
			let currentTime = 0;
			const tick = () => {
				currentTime += 1 / 60;
				const p = currentTime / time;
				const t = -0.5 * (Math.cos(Math.PI * p) - 1);
				if (p < 1) {
					requestAnimationFrame(tick);
					carousel.scrollLeft = currentPosition + (targetPosition - currentPosition) * t;
				} else {
					carousel.scrollLeft = targetPosition;
					this.scrollsnap = true;
				}
			};
			tick();
		},
		handleScroll(e) {
			window.clearTimeout(this.scrollTimer);
			this.scrollTimer = setTimeout(() => {
				const position = e.target.scrollLeft;
				let currentPage = Math.ceil(position / e.target.clientWidth);
				if (this.direction === 'rtl') {
					currentPage = Math.ceil((-1 * position) / e.target.clientWidth);
				}
				if (currentPage !== this.currentPage) {
					this.currentPage = currentPage;
				}
			}, this.debounceTime);
		},
		handleResize() {
			document.addEventListener('lichen:resize', () => {
				this.setConfiguration();
			});
		},
		openLightbox(assetId) {
			this.goToAsset = assetId;
			this.lightboxOpen = true;
		},
		closeLightbox() {
			this.lightboxOpen = false;
			this.mediaList.forEach((item, index) => {
				if (item.assetId === this.goToAsset) {
					this.$refs.slides[index].focus();
				}
			});
		},
		exoticSymbol(status) {
			switch (status.substring(0, 1).toLowerCase()) {
				case 'p':
					return 'exoticProvisional';
				case 'n':
					return 'exoticNaturalized';
				case 'e':
					return 'exoticEscapee';
			}
		},
		exoticTitle(status) {
			switch (status.substring(0, 1).toLowerCase()) {
				case 'p':
					return this.$t('exotic.categoryLabel', [this.$t('exotic.categoryP.title')]);
				case 'n':
					return this.$t('exotic.categoryLabel', [this.$t('exotic.categoryN.title')]);
				case 'e':
					return this.$t('exotic.categoryLabel', [this.$t('exotic.categoryX.title')]);
			}
		},
		openExoticModal(e) {
			const event = new CustomEvent('lichen:exotics-dialog', { detail: { opener: e.target } });
			document.dispatchEvent(event);
		},
		assetTitle(asset, includeComName = true) {
			if (asset.title) {
				return asset.title;
			}
			const title = [];
			let joinCharacters = ': ';
			if (asset.taxonomy.speciesCode && includeComName) {
				title.push(asset.taxonomy.comName);
			} else if (asset.tags) {
				if (asset.tags.includes('environmental')) {
					if (asset.mediaType === 'photo') {
						title.push(this.$t('mediaTag.tags.habitatPhoto'));
					}
					if (asset.mediaType === 'audio') {
						title.push(this.$t('mediaTag.tags.soundscape'));
					}
					if (asset.mediaType === 'video') {
						title.push(this.$t('mediaTag.tags.environmentalVideo'));
					}
				}
				else if (asset.tags.includes('experience')) {
					title.push(this.$t('mediaTag.tags.experience'));
				}
				else if (asset.tags.includes('other_animals')) {
					if (asset.taxonomy.speciesCode) {
						joinCharacters = '';
					} else {
						title.push(this.$t('mediaTag.tags.other_animals'));
					}
				}
				else if (asset.tags.includes('people')) {
					title.push(this.$t('mediaTag.tags.people'))
				}
			}
			if (asset.caption) {
				title.push(asset.caption);
			}
			return title.join(joinCharacters);
		},
	},
};
</script>

<style lang="scss">
.MediaFeed {
	width: 100%;
	.SectionHeading-link--right {
		display: inline-flex;
		align-items: center;
	}
	.MediaFeed-dots {
		line-height: 1;
		list-style: none;
		@include inline-md;
		display: none;
		@include breakpoint(medium) {
			display: inline-flex;
		}
		.MediaFeed-dot {
			background-color: $color-neutral-1;
			border-radius: 50%;
			height: $space-md;
			width: $space-md;
			@include inline-xs;
			&.active {
				background-color: $color-neutral-2;
			}
		}
	}
	.MediaFeed-nav {
		button {
			color: $color-link;
			&[disabled] {
				color: $color-neutral-2;
			}
		}
	}
	.MediaFeed-slides {
		list-style: none;
		margin: 0;
		display: flex;
		flex-wrap: nowrap;
		overflow-x: auto;
		width: auto;
		position: relative;
		//scroll-snap-type: x mandatory;
		// hide the scrollbar
		scrollbar-width: none;
		-ms-overflow-style: none;
		&::-webkit-scrollbar {
			width: 0;
			height: 0;
		}
	}
	.MediaFeed-slide {
		flex-grow: 0;
		flex-shrink: 0;
		@include inline-md;
		scroll-snap-align: start;
		scroll-snap-stop: always;
		outline: 0;
		.Media-content {
			@include stack-xs;
			position: relative;
			img {
				max-height: 12rem;
			}
			.MediaFeed-spectrogram {
				aspect-ratio: 32 / 11;
				//image-rendering: pixelated;
				&.pixelated {
					// delay image pixelating until loaded for Safari blurriness bug
					image-rendering: pixelated;
				}
			}
		}
		.MediaFeed-speciesLabel {
			@include stack-xs;
			display: flex;
			flex-wrap: nowrap;
			align-items: flex-start;
			gap: $space-xs;
			.Icon--warning, .Icon--users {
				fill: $brand-color-gold-11;
			}
		}
		.MediaFeed-lineClamp {
			display: -webkit-box;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}
		.Media-caption {
			@include text(2);
			color: $color-text-muted;
		}
		.MediaFeed-user {
			color: $color-text;
		}
		.MediaFeed-user,
		.MediaFeed-date {
			@include stack-xs;
		}
	}
	.MediaFeed-playButton {
		position: absolute;
		left: $space-sm;
		[dir='rtl'] & {
			left: auto;
			right: $space-sm;
		}
		bottom: $space-sm;
		@include inset-squish-sm;
		@include dropshadow-2;
		border-radius: $radius-sm;
		background-color: $color-primary-3;
		display: flex;
		align-items: center;
		.Icon {
			fill: $color-white;
			@include icon-sm;
		}
	}
	.MediaFeed-noMedia {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: $space-xl;
	}
}

.MediaFeed--reverse {
	.MediaFeed-dots {
		.MediaFeed-dot {
			background-color: $color-neutral-3;
			&.active {
				background-color: $color-neutral-2;
			}
		}
	}
	.MediaFeed-nav {
		button {
			color: $color-link-reverse;
			&[disabled] {
				color: $color-neutral-4;
			}
		}
	}
	.MediaFeed-slide {
		.Media-caption {
			color: $color-text-muted-reverse;
		}
		.MediaFeed-user {
			color: $color-text-reverse;
		}
		.MediaFeed-speciesLabel {
			.Icon--warning {
				fill: $brand-color-gold-8;
			}
		}
	}
}
</style>
